import type {
  SelfCheckoutFlowDescriptor,
  SelfCheckoutFlowStepContext,
  SelfCheckoutStepKey,
} from "./selfCheckoutFlow";

interface SelfCheckoutStep {
  key: SelfCheckoutStepKey;
  label: string;
}

/**
 * Builds the ordered, labelled step list for the progress header.
 *
 * Pure: step *presence* comes from the descriptor, step *labels* come from
 * the caller (each surface translates with its own i18n instance), so this
 * helper stays locale-free and both bundles can share it.
 */
const buildSelfCheckoutSteps = (
  descriptor: SelfCheckoutFlowDescriptor,
  context: SelfCheckoutFlowStepContext,
  labels: Record<SelfCheckoutStepKey, string>
): SelfCheckoutStep[] => {
  return descriptor.resolveSteps(context).map(key => ({ key, label: labels[key] }));
};

/**
 * 1-based stepper index of `stepKey` within `steps`, falling back to the
 * address step (mirrors the previous SelfCheckoutHeaderNav behavior for
 * unknown steps).
 */
const getSelfCheckoutStepNumber = (
  steps: SelfCheckoutStep[],
  stepKey: SelfCheckoutStepKey
): number => {
  const index = steps.findIndex(step => step.key === stepKey);
  if (index >= 0) return index + 1;

  const addressIndex = steps.findIndex(step => step.key === "address");

  return addressIndex >= 0 ? addressIndex + 1 : 1;
};

export type { SelfCheckoutStep };
export { buildSelfCheckoutSteps, getSelfCheckoutStepNumber };
